(ns app.front.dashboard.search
  (:require [app.domain.workspace.model :as workspace-model]
            [app.front.dashboard.map :as dashboard-map]
            [app.front.entities :as entities]
            [app.front.municipality :as municipality]
            [app.front.search-counts :as search-counts]
            [app.front.search-result :as sr]
            [app.front.ui :as ui]
            [app.front.ui.common-tags :as ct]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.filter-common :as filter-common]
            [app.front.user :as front.user]
            [app.front.util :refer [for-frag]]
            [app.front.view.navigation :as navigation]
            [app.query.filter-util :as filter-util]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.front.session :as session]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema-tools.core :as st]
            [schema.core :as s]))

(s/defschema SearchMode
  (st/merge
    {:name s/Keyword

     ;; Commands used for this kind of search
     :search-command        s/Keyword
     :search-config-command s/Keyword
     :save-filter-command   s/Keyword
     :remove-filter-command s/Keyword

     ;; Translation keys specific to this kind of search
     :title    s/Keyword
     :subtitle s/Keyword

     ;; Functions for reordering data and rendering search results
     ;;; Transform the attribute and value produced by calling :search-config-command into a value that can be used for lookup
     ;;; Necessary because :user/search-config returns both group ids and names, but lookup should be performed by id
     :filter-value         (s/pred ifn?)
     ;;; Transform the value used for lookup into a string that is displayed to the user (e.g. turn the group id back into the group name)
     :val->str-fn          (s/pred ifn?)
     ;;; Render a search result
     :render-search-result (s/pred ifn?)
     ;;; Generate a unique id from an Elastic result
     :id-fn                (s/pred ifn?)

     ;; Other
     :filter-subtype              s/Keyword ;; filter.subtype/*
     :filter-condition-attributes [s/Keyword]
     :default-view                (s/enum :dashboard-search/list-view :dashboard-search/map-view)}
    (st/optional-keys
      {:map-search-command              s/Keyword
       ;;; Dispatch an event after opening a map result
       :map-result-open-event           s/Keyword
       ;;; Dispatch an event after dismissing a map result
       :map-result-dismiss-event        s/Keyword
       ;;; Dispatch an event with the coordinates of a map click on no result
       :map-point-click-event           s/Keyword
       ;;; Dispatch an event with the point id after the map drops a selected point
       :map-point-dismiss-event         s/Keyword
       ;;; Render the side panel of a map point selected with `dashboard-map/select-point`
       :render-map-point-view           (s/pred ifn?)
       ;;; Render mode-specific overlays outside the result layout
       :render-overlays                 (s/pred ifn?)
       ;;; Feature flags mapped to the filter attributes they gate
       :feature-gated-filter-attributes {s/Keyword #{s/Keyword}}
       ;;; Clean up mode-specific state when leaving the mode
       :cleanup-event                   [s/Any]
       ;;; Group map search results into groups
       :cluster-fn                      (s/pred ifn?)
       ;;; Render the grouped results
       :render-clustered-results        (s/pred ifn?)
       ;;; Columns of the list view, as a fn of {:keys [tr lang format-date val->str-fn sort-options]}
       :list-view-cols                  (s/pred ifn?)
       ;;; Row link of the list view, as a fn of the same options
       :list-view-link                  (s/pred ifn?)
       ;;; Row click of the list view, as a fn of the search result; for results that have no
       ;;; href to open, e.g. the complaint modal
       :list-view-row-click             (s/pred ifn?)
       ;;; Row actions of the list view, as a fn of the same options and a search result
       :list-view-actions               (s/pred ifn?)
       :button-filters                  #{s/Keyword}
       :prefers-list-view-flag          (apply s/enum user-model/config-flags)})))


(def valid-search-mode! (s/validator SearchMode))


(def default-search-mode
  {:button-filters #{}
   :default-view   :dashboard-search/list-view})


(def initial-search-event [::search {:keep-results?   false
                                     :initial-search? true
                                     :skip            0}])


(def new-search-event [::search {:keep-results? false
                                 :skip          0}])


(def load-more-event [::search {:keep-results? true}])


(defn search-mode-name [db]
  (-> db ::search-mode :name))


(defn workspace-search? [db]
  (= (search-mode-name db) :workspace))


(defn data-path [db & path]
  (concat [::data (search-mode-name db)]
          path))

(defn data-path-for [mode-name & path]
  (concat [::data mode-name] path))

(defn get-data [db & path]
  (get-in db (apply data-path db path)))


(defn current-view
  "The view the results are shown in, the search mode's default until the user picks one.

  Must be used instead of a bare `::selected-view` lookup: a mode that defaults to the map view
  has no selected view before the user switches views, and reading `nil` as the list view makes
  the map and the list disagree about which search has been run."
  [db]
  (or (get-data db ::selected-view)
      (-> db ::search-mode :default-view)))


(def result-tab->selected-view
  "The view each of the result tabs shows: both the list and the grid render the results of the
  regular (non-map) search, so switching between those two needs no new search. The tabs of the
  map and the list are named after the view they show, the grid tab is the only one that is not."
  {:dashboard-search/map-view  :dashboard-search/map-view
   :dashboard-search/list-view :dashboard-search/list-view
   :dashboard-search/grid-view :dashboard-search/list-view})


(rf/reg-sub ::search-mode
  :-> ::search-mode)


(rf/reg-sub ::button-filters
  :<- [::search-mode]
  :<- [::ff/enabled? :feature-flag.id/dashboard-search-functions]
  (fn [[search-mode search-functions-enabled?]]
    (if (and (= (:name search-mode) :workspace)
             search-functions-enabled?)
      #{:main-permit-type}
      (:button-filters search-mode))))


(rf/reg-sub ::default-view
  :<- [::search-mode]
  :-> :default-view)


(defn- noninitial-workspace-mode? [search-mode first-init?]
  (and (not first-init?)
       (= (:name search-mode) :workspace)
       (ff/enabled? :feature-flag.id/dashboard-search-functions)))


(defn- retriggering-filter-attributes [db]
  (-> db
      (get-data ::filter-values)
      (select-keys workspace-model/retriggering-filter-condition-attributes)))


(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ {mode-name :name :as search-mode} {:keys [config-data] :as opts}]]
    (let [search-mode (merge default-search-mode search-mode)
          first-init? (not (contains? (get-in db [::data mode-name]) ::search-config))
          request-id  (random-uuid)]
      {:comm/command (p/assoc-if
                       {:command          (:search-config-command search-mode)
                        :on-success       [::search-config.success mode-name request-id first-init? search-mode
                                           (select-keys opts [:search-event :always-search?])]
                        :finally-dispatch [::search-config.done mode-name request-id]}
                       :data (or config-data
                                 (when (noninitial-workspace-mode? search-mode first-init?)
                                   (retriggering-filter-attributes db))))
       :db           (-> db
                         (assoc ::search-mode (valid-search-mode! search-mode))
                         (assoc-in [::data mode-name ::loading?] true)
                         ;; Ignore results from before this init, even for the same mode.
                         (update-in [::data mode-name] dissoc ::search-request-id)
                         (assoc-in [::data mode-name ::search-config-request-id] request-id))
       :dispatch     [::dashboard-map/init search-mode]})))


(defn default-data-map [search-config]
  {::filter-values (-> search-config :default-selected-values)
   :sort-condition (-> search-config :sort-condition)
   :skip           0
   :limit          50})

(defn initial-revision
  "We keep track of revision for each attribute separately
  so that we have fine-grained control over re-rendering of
  multi-select components."
  [filter-condition-attributes]
  (reduce
    (fn [acc attr]
      (assoc acc attr 0))
    {}
    filter-condition-attributes))

(defn revision-update-fn
  "Get revision update function.

  If `attr` is provided, does not increment the revision
  for that attribute, this can be used to prevent unnecessary
  closing of multi-select components."
  [attr]
  (if attr
    (fn [revision]
      (let [old-attr-val (get revision attr)]
        (merge (p/map-vals inc revision)
               {attr old-attr-val})))
    (partial p/map-vals inc)))

(defn update-revision
  ([db]
   (update-revision db nil))
  ([db attr]
   (update-in db (data-path db ::revision) (revision-update-fn attr))))

(defn filter-values-from-options
  "Normalize search-config filter options to the values used in search requests."
  [filter-values filter-value-fn]
  (reduce-kv (fn [result attr options]
               (assoc result attr (into #{} (map #(filter-value-fn attr %) options))))
             {}
             filter-values))

(defn- merge-filter-values [current-values new-values filter-value-fn]
  (let [available-values (filter-values-from-options new-values filter-value-fn)]
    (reduce-kv (fn [result attr values]
                 (if-some [values (not-empty (set/intersection values (get available-values attr #{})))]
                   (assoc result attr values)
                   result))
               {}
               current-values)))

(defn set-search-config [db first-init? {mode-name :name :as search-mode} {:keys [preferred-view] :as response}]
  (cond-> (assoc-in db (data-path-for mode-name ::search-config) (dissoc response :user/filters :preferred-view))
    first-init?
    (-> (update-in (data-path-for mode-name)
                   merge
                   {::revision (initial-revision (:filter-condition-attributes search-mode))}
                   (default-data-map response))
        (assoc-in (data-path-for mode-name ::selected-view) preferred-view))

    (noninitial-workspace-mode? search-mode first-init?)
    (-> (assoc-in (data-path-for mode-name ::filter-values)
                  (merge-filter-values (get-in db (data-path-for mode-name ::filter-values))
                                       (:filter-values response)
                                       (:filter-value search-mode)))
        (update-in (data-path-for mode-name ::revision) (revision-update-fn nil)))))

;; `opts` are the `::init` options that the search config response is needed for:
;;   `:search-event`   - dispatched instead of the plain initial search, e.g. to restore a saved search
;;   `:always-search?` - search right away also for admins. They are left without an initial search
;;                       by default, to spare Elasticsearch the unfiltered searches of a user that
;;                       has access to everything. Callers whose UI expects results to exist (the
;;                       search page) ask for the search instead of coping with the empty state.
(rf/reg-event-fx ::search-config.success
  (fn [{:keys [db]} [_ mode-name request-id first-init? search-mode {:keys [search-event always-search?]} status
                     {:keys [domain-group-ids] :as response}]]
    (when (= request-id (get-in db (data-path-for mode-name ::search-config-request-id)))
      (let [active?            (= mode-name (search-mode-name db))
            admin?             (-> db :session/user session/admin?)
            map-config-effects (when (and active? (not admin?))
                                 (map (fn [group-id]
                                        [:dispatch [::municipality/load-map-config group-id]])
                                      domain-group-ids))]
        {:db (set-search-config db first-init? search-mode response)
         :fx (concat [[:dispatch [::entities/merge-entity-db status (:user/filters response)]]]
                     (when active?
                       (cond
                         search-event
                         (concat [[:dispatch search-event]] map-config-effects)

                         (or always-search? (not admin?))
                         (concat [[:dispatch initial-search-event]] map-config-effects)

                         (noninitial-workspace-mode? search-mode first-init?)
                         [[:dispatch initial-search-event]])))}))))

(rf/reg-event-db ::search-config.done
  (fn [db [_ mode-name request-id]]
    (if (= request-id (get-in db (data-path-for mode-name ::search-config-request-id)))
      (update-in db (data-path-for mode-name)
                 #(-> %
                      (assoc ::loading? false)
                      (dissoc ::search-config-request-id)))
      db)))


(rf/reg-event-fx ::fetch-search-filter-values
  (fn [{:keys [db]} [_ attr value]]
    {:comm/command
     {:command    :user/search-filter-values
      :data       (-> db
                      retriggering-filter-attributes
                      (p/assoc-if attr value))
      :on-success [::fetch-search-filter-values.success (search-mode-name db) attr]}}))


(rf/reg-event-fx ::fetch-search-filter-values.success
  (fn [{:keys [db]} [_ mode-name attr _status {:keys [filter-values]}]]
    (when (= mode-name (search-mode-name db))
      (let [new-filter-values (merge-filter-values (get-data db ::filter-values)
                                                   filter-values
                                                   (-> db ::search-mode :filter-value))]
        {:db (-> db
                 (assoc-in (data-path db ::search-config :filter-values) filter-values)
                 (assoc-in (data-path db ::filter-values) new-filter-values)
                 (update-revision attr))
         :fx [[:dispatch new-search-event]]}))))


(defn- clear-filters [db]
  {:db (-> db
           (assoc-in (data-path db ::filter-values) {})
           update-revision)
   :fx [[:dispatch (if (and (ff/enabled? :feature-flag.id/dashboard-search-functions)
                            (workspace-search? db))
                     [::fetch-search-filter-values]
                     new-search-event)]]})

(rf/reg-event-fx ::clear-filters
  (fn [{:keys [db]} _]
    (clear-filters db)))

(rf/reg-event-fx ::clear-search
  (fn [{:keys [db]} _]
    (clear-filters (p/dissoc-in db (data-path db ::search-term)))))


(rf/reg-sub ::data
  :-> get-data)


(rf/reg-sub ::search-config
  :<- [::data]
  :-> ::search-config)


(defn not-sortable-result? [attr result]
  (or (nil? (get result attr))
      (and (= attr :application/target-time-date)
           (not (sr/workspace-show-target-time? result)))))


(defn search-sort-fn [attr dir a b]
  (cond
    (= attr :approval-due-date) 0 ; Don't do complex sorting here
    (= attr :review-due-date) 0 ; Don't do complex sorting here either
    (= attr :pre-con-expiration-date) 0 ; Don't do complex sorting here either
    (every? #(not-sortable-result? attr %) [a b]) 0
    (not-sortable-result? attr a) 1 ; Always put the ones without the attribute as last
    (not-sortable-result? attr b) -1
    :else
    (cond-> (compare (attr a) (attr b))
      (= dir :desc) (* -1))))


(defn hidden-filter-attributes
  "Attributes gated by disabled feature flags. Accepts a flag predicate or enabled flag set."
  [{:keys [feature-gated-filter-attributes]} feature-enabled?]
  (->> feature-gated-filter-attributes
       (remove (fn [[flag-id _]] (feature-enabled? flag-id)))
       (mapcat val)
       set))

(defn- feature-gated-filter-values
  ([search-mode filter-values]
   (feature-gated-filter-values search-mode ff/enabled? filter-values))
  ([search-mode feature-enabled? filter-values]
   (apply dissoc filter-values (hidden-filter-attributes search-mode feature-enabled?))))


(defn format-search-request [db]
  (let [search-mode (::search-mode db)]
    (p/assoc-if {:conditions (->> (merge (get-data db ::implicit-filter-values)
                                         (feature-gated-filter-values search-mode
                                                                      (get-data db ::filter-values)))
                                  filter-common/filter-values->conditions)}
                :search-term (get-data db ::search-term))))


(rf/reg-event-fx ::search
  (fn [{{{:keys [search-command]} ::search-mode :as db} :db}
       [_ {:keys [keep-results? skip initial-search?]}]]
    (if (= (current-view db) :dashboard-search/map-view)
      (when-not initial-search?
        ;; Map component will do the initial search by itself after loading.
        ;; A selected map point was resolved for the previous filters and scope.
        (p/assoc-if {:db       (dashboard-map/deselect-point db)
                     :dispatch [::dashboard-map/map-search (format-search-request db) nil]}
                    :fx (dashboard-map/point-dismiss-fx db)))
      (let [request-id (random-uuid)
            mode-name  (search-mode-name db)
            new-db     (cond-> (-> db
                                   (search-counts/start-request mode-name)
                                   (assoc-in (data-path db ::search-in-progress?) true)
                                   (assoc-in (data-path db ::search-request-id) request-id))
                         skip (assoc-in (data-path db :skip) skip))]
        {:comm/command {:command          search-command
                        :data             (merge (format-search-request new-db)
                                                 (select-keys (get-data db) [:sort-condition :skip :limit]))
                        :on-success       (search-counts/response-event
                                            new-db mode-name
                                            [::search.success mode-name request-id keep-results?])
                        :on-error         (search-counts/error-event new-db mode-name)
                        :finally-dispatch (search-counts/done-event new-db mode-name
                                                                    [::search.done mode-name request-id])}
         :db           new-db}))))


(rf/reg-event-db ::search.success
  (fn [{{:keys [id-fn]} ::search-mode :as db}
       [_ mode-name request-id keep-results? _ {:keys [total-count results]}]]
    (if (and (= mode-name (search-mode-name db))
             (= request-id (get-in db (data-path-for mode-name ::search-request-id))))
      (let [current-results (when keep-results?
                              (let [ws-ids-in-resp (->> results
                                                        (map id-fn)
                                                        set)]
                                (->> (get-data db ::result :results)
                                     ; Prevent any duplicates
                                     (remove #(contains? ws-ids-in-resp (id-fn %))))))
            [sort-attr dir] (get-data db :sort-condition)]
        (->> (concat current-results results)
             (sort (partial search-sort-fn sort-attr dir))
             (hash-map :total-count total-count :results)
             (assoc-in db (data-path db ::result))))
      db)))


(rf/reg-event-db ::search.done
  (fn [db [_ mode-name request-id]]
    (if (= request-id (get-in db (data-path-for mode-name ::search-request-id)))
      (assoc-in db (data-path-for mode-name ::search-in-progress?) false)
      db)))


(rf/reg-event-db ::toggle-workspace-notifications
  (fn [db [_ workspace-id notifications-disabled?]]
    (update-in db
               (data-path-for :workspace ::result :results)
               (partial mapv (fn [workspace]
                               (if (= workspace-id (:workspace/id workspace))
                                 (assoc workspace :notifications-disabled? (not notifications-disabled?))
                                 workspace))))))


(rf/reg-event-fx ::load-more
  (fn [{:keys [db]} _]
    {:db       (assoc-in db (data-path db :skip) (count (get-data db ::result :results)))
     :dispatch load-more-event}))


(rf/reg-event-fx ::freetext-search-event
  (fn [{:keys [db]} [_ term]]
    {:dispatch-debounce [{:id      ::do-freetext-search
                          :timeout 500
                          :action  :dispatch
                          :event   new-search-event}]
     :db                (if (str/blank? term)
                          (p/dissoc-in db (data-path db ::search-term))
                          (assoc-in db (data-path db ::search-term) term))}))


(defn- set-filter-value [db attr value]
  {:db (if (seq value)
         (assoc-in db (data-path db ::filter-values attr) value)
         (p/dissoc-in db (data-path db ::filter-values attr)))
   :fx [[:dispatch (if (and (ff/enabled? :feature-flag.id/dashboard-search-functions)
                            (contains? workspace-model/retriggering-filter-condition-attributes attr)
                            (workspace-search? db))
                     [::fetch-search-filter-values attr value]
                     new-search-event)]]})


(rf/reg-event-fx ::set-filter-value
  (fn [{:keys [db]} [_ attr value]]
    (set-filter-value db attr value)))

;; Merges with the range in app-db, the one a date picker rendered with can be stale
(rf/reg-event-fx ::set-date-range-bound
  (fn [{:keys [db]} [_ attr k date]]
    (let [date-range (-> (get-data db ::filter-values attr)
                         first
                         (dissoc k)
                         (p/assoc-if k date))]
      (set-filter-value db attr (when (seq date-range)
                                  #{date-range})))))


(rf/reg-event-fx ::select-saved-filter
  (fn [{:keys [db]} [_ filter]]
    {:db (-> (assoc-in db
                       (data-path db ::filter-values)
                       (feature-gated-filter-values (::search-mode db)
                                                    (filter-util/filter-entity->search-values-map filter)))
             update-revision)
     :fx [[:dispatch (if (and (ff/enabled? :feature-flag.id/dashboard-search-functions)
                              (workspace-search? db))
                       [::fetch-search-filter-values]
                       new-search-event)]]}))


(rf/reg-event-fx ::save-filter
  (fn [{:keys [db]} [_ filter-name]]
    {:comm/command {:command          (-> db ::search-mode :save-filter-command)
                    :data             {:filter/name       filter-name
                                       :filter/conditions (->> (format-search-request db)
                                                               :conditions)}
                    :on-success       [::save-filter.success]
                    :finally-dispatch [::save-filter.done]}
     :db           (assoc-in db (data-path db ::saving-filter?) true)}))


(rf/reg-event-fx ::save-filter.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :dashboard-search/save-success]]}))


(rf/reg-event-db ::save-filter.done
  #(p/dissoc-in % (data-path % ::saving-filter?)))


(rf/reg-event-fx ::save-quick-filter
  (fn [{:keys [db]} [_ idx filter-id existing-filter-id]]

    {:db           (cond-> db
                     filter-id (assoc-in (get-data db ::search-config :user/quick-filters filter-id) 0))
     :comm/command {:command    (if filter-id
                                  :user/save-quick-filter
                                  :user/remove-quick-filter)
                    :data       (merge {:filter/subtype (-> db ::search-mode :filter-subtype)}
                                       (if filter-id
                                         {:id            filter-id
                                          :ordered/order idx}
                                         ; Remove existing placement
                                         {:id existing-filter-id}))
                    :on-success [::save-quick-filter.success]}
     :dispatch-n   (concat (when filter-id
                             [[::entities/update-entity filter-id {:ordered/order idx}]])
                           (when existing-filter-id
                             [[::entities/update-entity existing-filter-id {:ordered/order nil}]]))}))


(rf/reg-event-fx ::save-quick-filter.success
  (fn [{:keys [db]} [_ status {:keys [id total tx-changes remove-id]}]]
    {:db         (cond-> db
                   total (assoc-in (data-path db ::search-config :user/quick-filters id) total)
                   remove-id (p/dissoc-in (data-path db ::search-config :user/quick-filters remove-id)))
     :dispatch-n [[::entities/apply-changes status tx-changes]
                  [:notifications/add :toast :success :success :dashboard-search/save-success]]}))


(rf/reg-event-fx
  ::delete-filter
  (fn [{:keys [db]} [_ filter-id]]
    {:comm/command {:command    (-> db ::search-mode :remove-filter-command)
                    :data       {:id filter-id}
                    :on-success [::delete-filter.success]}}))


(rf/reg-event-fx
  ::delete-filter.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::clear-search]
                  [::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :dashboard-search/delete-success]]}))


(rf/reg-event-fx ::set-sort-condition
  (fn [{:keys [db]} [_ sort-condition]]
    {:db       (assoc-in db (data-path db :sort-condition) sort-condition)
     :dispatch new-search-event}))


(rf/reg-event-fx ::set-selected-view
  (fn [{:keys [db]} [_ view]]
    (let [flag (-> db ::search-mode :prefers-list-view-flag)]
      {:db (-> (assoc-in db (data-path db ::selected-view) view)
               (assoc ::dashboard-map/data {}))
       :fx (conj (vec (dashboard-map/point-dismiss-fx db))
                 [:dispatch initial-search-event]
                 (when flag
                   [:dispatch [::front.user/set-config-flag
                               flag
                               (-> db ::search-mode :default-view (not= view))]]))})))


(rf/reg-event-fx ::set-result-tab
  (fn [{:keys [db]} [_ tab]]
    (let [view (result-tab->selected-view tab)]
      (p/assoc-if {:db (assoc-in db (data-path db ::result-tab) tab)}
                  ;; Switching between the list and the grid keeps the results as they are.
                  :dispatch (when (not= view (current-view db))
                              [::set-selected-view view])))))


(rf/reg-sub ::filter-values
  :<- [::data]
  :<- [::search-mode]
  :<- [::ff/enabled-flags]
  (fn [[data search-mode enabled-flags]]
    (feature-gated-filter-values search-mode enabled-flags (::filter-values data))))


(rf/reg-sub ::result
  :<- [::data]
  :-> ::result)


(rf/reg-sub ::search-term
  :<- [::data]
  :-> ::search-term)


(rf/reg-sub ::revision
  :<- [::data]
  :-> ::revision)


(rf/reg-sub ::sort-condition
  :<- [::data]
  :-> :sort-condition)


(rf/reg-sub ::saving-filter?
  :<- [::data]
  :-> ::saving-filter?)


(rf/reg-sub ::config-loading?
  :<- [::data]
  :-> ::loading?)


(rf/reg-sub ::search-in-progress?
  :<- [::data]
  :-> ::search-in-progress?)

(rf/reg-sub ::has-results?
  :<- [::result]
  (fn [result]
    (-> result :results seq boolean)))

(rf/reg-sub ::current-count
  :<- [::result]
  (fn [result]
    (-> result :results count)))

(rf/reg-sub ::result-total-count
  :<- [::data]
  :<- [::current-view]
  (fn [[data view]]
    (if (= view :dashboard-search/map-view)
      (<sub [::dashboard-map/total-count])
      (-> data ::result :total-count))))

(rf/reg-sub ::user-filters
  :<- [::entities/context]
  :<- [::search-mode]
  (fn [[ctx search-mode]]
    (->> (entities/find-entities ctx {:entity/type    :entity.type/filter
                                      :filter/subtype (:filter-subtype search-mode)})
         (sort-by :filter/name))))


(rf/reg-sub ::selected-saved-filter
  :<- [::user-filters]
  :<- [::filter-values]
  (fn [[user-filters filter-values]]
    (-> user-filters
        (p/find-first #(= (filter-util/filter-entity->search-values-map %)
                          filter-values)))))


(rf/reg-sub ::user-quick-filters
  :<- [::search-config]
  :<- [::user-filters]
  (fn [[search-config filters]]
    (->> search-config
         :user/quick-filters
         (map (fn [[id _]]
                [(p/find-first filters {:db/id id})
                 (get-in search-config [:user/quick-filters id])]))
         (sort-by (comp :ordered/order first)))))


(rf/reg-sub ::grand-total
  :<- [::search-config]
  :<- [::data]
  (fn [[search-config data] _]
    ;; Only a number to show next to the title: whether the user has access to anything is asked
    ;; from ::no-access?, which never guesses from the result count.
    (or (:grand-total-count search-config)
        ;; Admins do not get the grand total as they have access to everything, so the size of the
        ;; latest result set has to do. The map result count must not be used here, as it is zero
        ;; until the map has run its own search.
        (-> data ::result :total-count))))


;; True when the user has access to nothing at all, which only the count from the search config can
;; say: a search that matched nothing has a zero result count too. Used by the dashboard widget;
;; the search page shows its results and an ordinary 'no results' text instead.
(rf/reg-sub ::no-access?
  :<- [::search-config]
  (fn [search-config _]
    (= 0 (:grand-total-count search-config))))


(rf/reg-sub ::selected-view
  :<- [::data]
  :-> ::selected-view)

(rf/reg-sub ::current-view
  :<- [::selected-view]
  :<- [::default-view]
  (fn [[selected default]]
    (or selected default)))

(rf/reg-sub ::result-tab
  :<- [::data]
  :<- [::current-view]
  :<- [::search-mode]
  (fn [[data view {:keys [map-search-command]}] _]
    (let [tab (::result-tab data)]
      (cond
        ;; A search mode without a map search has no map tab to select.
        (and (= view :dashboard-search/map-view) (not map-search-command)) :dashboard-search/grid-view
        ;; The view can also change without the tab, e.g. when the search config brings the
        ;; user's preferred view, so the view is what the selected tab is checked against.
        (= view (result-tab->selected-view tab))                           tab
        (= view :dashboard-search/map-view)                                :dashboard-search/map-view
        :else                                                              :dashboard-search/grid-view))))


(rf/reg-sub ::map-center
  :<- [::search-config]
  :-> :default-map-center)


(rf/reg-sub ::domain-group-ids
  :<- [::search-config]
  :-> :domain-group-ids)


(rf/reg-sub ::map-search-opts
  :-> format-search-request)


(defn group-or-user->name [entity-like]
  (if (= (:entity/type entity-like) :entity.type/group)
    (:group/long-name entity-like)
    (user-model/full-name entity-like)))


(defn db-id->name [search-config attr tr id]
  (if (keyword? id)
    (tr [id])
    (-> search-config
        :filter-values
        attr
        (p/find-first {:db/id id})
        group-or-user->name)))


(rf/reg-sub ::val->str-fn
  :<- [::search-mode]
  :<- [::search-config]
  :<- [:tr]
  (fn [[{:keys [val->str-fn]} search-config tr] _]
    (partial val->str-fn search-config tr)))


(rf/reg-sub ::selectable-filter-conditions
  :<- [::search-config]
  :<- [::search-mode]
  :<- [::button-filters]
  :<- [::ff/enabled-flags]
  (fn [[{:keys [filter-values]} {:keys [filter-value filter-condition-attributes] :as search-mode}
        button-filters enabled-flags]]
    (let [filter-values (feature-gated-filter-values search-mode enabled-flags filter-values)]
      (->> filter-condition-attributes
           (remove button-filters)
           (filter (fn [attribute]
                     (seq (get filter-values attribute))))
           (map (fn [attribute]
                  {:filter.condition/attribute attribute
                   :values                     (->> attribute
                                                    filter-values
                                                    (map (fn [v] (filter-value attribute v))))}))))))

(rf/reg-sub ::button-filter-conditions
  :<- [::search-config]
  :<- [::search-mode]
  :<- [::button-filters]
  :<- [::ff/enabled-flags]
  (fn [[{:keys [filter-values]} {:keys [filter-value] :as search-mode} button-filters enabled-flags]]
    (let [filter-values (feature-gated-filter-values search-mode enabled-flags filter-values)]
      (->> button-filters
           (filter (fn [attribute]
                     (< 1 (count (get filter-values attribute)))))
           (map (fn [attribute]
                  {:filter.condition/attribute attribute
                   :values                     (->> attribute
                                                    filter-values
                                                    (map (fn [v] (filter-value attribute v))))}))))))

(defn- selected-button-classes [bg-color]
  ["border-transparent" "text-ui-white" bg-color])

(defn- unselected-button-classes [border-color text-color]
  ["bg-ui-white" border-color text-color])

(def default-selected-button-classes
  (selected-button-classes "bg-ui-blue-110"))

(def default-unselected-button-classes
  (unselected-button-classes "border-ui-blue-110" "text-ui-blue-110"))

(defmulti button-style :value)

(defmethod button-style :default
  [{:keys [selected?]}]
  {:class (if selected?
            default-selected-button-classes
            default-unselected-button-classes)})

(defmethod button-style :permit-type/B
  [{:keys [value selected?]}]
  {:class (if selected?
            default-selected-button-classes
            default-unselected-button-classes)
   :icon  (ui/permit-type-icon value)})

(defmethod button-style :permit-type/P
  [{:keys [value selected?]}]
  {:class (if selected?
            (selected-button-classes "bg-ui-blue-150")
            (unselected-button-classes "border-ui-blue-150" "text-ui-blue-150"))
   :icon  (ui/permit-type-icon value)})

(defmethod button-style :permit-type/CE
  [{:keys [value selected?]}]
  {:class (if selected?
            (selected-button-classes "bg-ui-red-140")
            (unselected-button-classes "border-ui-red-140" "text-ui-red-140"))
   :icon  (ui/permit-type-icon value)})

(defmethod button-style :permit-type/PW
  [{:keys [value selected?]}]
  {:class (if selected?
            default-selected-button-classes
            default-unselected-button-classes)
   :icon  (ui/permit-type-icon value)})

(defmethod button-style :permit-type/PWO
  [{:keys [value selected?]}]
  {:class (if selected?
            default-selected-button-classes
            default-unselected-button-classes)
   :icon  (ui/permit-type-icon value)})

(defn filter-button
  [{:keys [attr value current-values]}]
  (let [selected?            (contains? current-values value)
        {:keys [class icon]} (button-style (util/mmap value selected?))
        label-fn             ((<sub [::val->str-fn]) attr)
        label                (label-fn value)
        on-click-fn          #(>evt [::set-filter-value attr
                                     (if selected?
                                       (disj current-values value)
                                       (conj current-values value))])]
    (if (not (ff/enabled? :feature-flag.id/new-tags))
      [:button.flex.items-center.rounded-full.border-solid.px-4.h-10
       {:class    (concat class ["hover:bg-ui-night-sky-100" "hover:text-ui-white"])
        :on-click on-click-fn}
       [:div.flex-1.font-semibold.uppercase
        label]
       (when icon
         [:> icon
          {:class-name "ml-2 fill-current"}])]
      [ct/tag {:label-text label
               :selected?  selected?
               :on-click   on-click-fn
               :icon       icon}])))

(defn filter-buttons
  []
  (let [conditions    (<sub [::button-filter-conditions])
        filter-values (<sub [::filter-values])]
    [:div.flex.flex-col.w-full.px-2.space-y-2.my-2
     (for-frag [{:filter.condition/keys [attribute] :as condition} conditions]
       [:div.flex.flex-wrap.flex-col.md:flex-row.gap-2
        (let [current-values (or (-> filter-values attribute) #{})]
          (for-frag [value (:values condition)]
            [filter-button {:attr           attribute
                            :current-values current-values
                            :value          value}]))])]))


(defn title
  [tr]
  (let [;; Admins get no grand total, for them the size of the current result set is the best
        ;; number to show next to the title.
        total                    (or (<sub [::grand-total])
                                     (<sub [::result-total-count]))
        {:keys [title subtitle]} (<sub [::search-mode])]
    [:div.mb-8
     [:h2.flex-1
      (tr [title])
      (when total
        (str " (" total ")"))]
     [:div.mt-2 (tr [subtitle])]]))

(defn freetext-search
  [tr]
  [:div {:class filter-common/condition-width-classes}
   [ui/search {:id                      "authority-dashboard-applications-search"
               :class                   "h-4"
               :label-text              (tr [:dashboard-search/search])
               :placeholder             (tr [:dashboard-search/search-placeholder])
               :close-button-label-text (tr [:search/empty])
               :value                   (<sub [::search-term])
               :on-change               #(rf/dispatch-sync [::freetext-search-event (-> % .-target .-value str)])
               :search-in-progress?     (<sub [::search-in-progress?])
               :loading-style           {:top "3px"}
               :loading-text            (tr [:search/loading])
               :clear-fn                #(>evt [::freetext-search-event ""])
               :light                   true}]])


(defn filter-selects
  ([tr config-loading?]
   (filter-selects tr config-loading? {:light? true}))
  ([tr config-loading? {:keys [light? conditions]
                        :or   {conditions (<sub [::selectable-filter-conditions])}}]
   [filter-common/dashboard-filter-selects (util/mmap {:conditions               conditions
                                                       :filter-values            (<sub [::filter-values])
                                                       :revision                 (<sub [::revision])
                                                       :val->str-fn              (<sub [::val->str-fn])
                                                       :set-filter-value-evt     ::set-filter-value
                                                       :set-date-range-bound-evt ::set-date-range-bound
                                                       :light?                   light?}
                                                      tr config-loading?)]))


(defn search-tools
  [tr config-loading?]
  [:div.flex.flex-wrap.-mx-2.mt-2
   (when-not config-loading?
     [:<>
      [filter-buttons]
      [freetext-search tr]])
   [filter-selects tr config-loading?]])


(defn title-results
  [tr]
  (let [total-count  (<sub [::result-total-count])
        in-progress? (<sub [::search-in-progress?])]
    [:div.flex
     [:h3.mb-0
      (tr [:dashboard-search/results])
      (when (and total-count
                 (not in-progress?))
        (str " (" total-count ")"))]
     (when in-progress?
       (cc/loading-spinner {:with-overlay false
                            :small        true
                            :class-name   "ml-2 my-1"}))]))

(defn search-results
  [render-search-result]
  (let [{:keys [results]}  (<sub [::result])
        [sort-attribute _] (<sub [::sort-condition])]
    [:div.bx--row
     (for-frag [result results]
       (render-search-result result {:map-search?           false
                                     :show-review-due-date? (= sort-attribute :review-due-date)}))]))

(defn results-placeholder
  "Shown instead of the results while a search is running or when it found nothing."
  [tr]
  (if (<sub [::search-in-progress?])
    (cc/loading-spinner {:with-overlay false})
    [:p (tr [:dashboard-search/no-results])]))

(defn back-to-top-button
  [tr]
  [:div.bx--row>div.bx--col-xs-12.flex.justify-end
   [:button.btn-back-to-top
    {:on-click #(>evt [::navigation/scroll-to-top])}
    [:span.mr-2.whitespace-nowrap (tr [:go-top])]
    (cc/icon {:icon        i/chevron-top
              :description (tr [:go-top])
              :fill        "currentcolor"})]])

(defn load-more
  [tr]
  (let [current-count (<sub [::current-count])
        total-count   (<sub [::result-total-count])]
    (when (> total-count current-count)
      [:div.bx--row>div.bx--col-xs-12.flex.justify-center
       [:button.cursor-pointer.p-4.flex.flex-col.items-center.border-0.bg-ui-white
        {:on-click #(>evt [::load-more])}
        (cc/icon {:icon        i/refresh
                  :class-name  "mb-4"
                  :description (tr [:dashboard-search/load-more] [(- total-count current-count)])})
        [:span.semibold (tr [:dashboard-search/load-more] [(- total-count current-count)])]]])))

(defn workspace-search [tr]
  (let [results?        (<sub [::has-results?])
        config-loading? (<sub [::config-loading?])
        user-filters    (<sub [::user-filters])
        selected-filter (<sub [::selected-saved-filter])
        selected-view   (<sub [::selected-view])
        search-mode     (<sub [::search-mode])
        admin?          (<sub [:session/admin?])]
    [:section.dashlet.dashboard__search
     [title tr]

     [eb/error-boundary
      [:div.bg-ui-light-gray.px-4.pb-4.pt-2.rounded
       [filter-common/dashboard-user-filter-controls (util/mmap {:title             :dashboard-search/search
                                                                 :selected-filter   selected-filter
                                                                 :default-filters?  (empty? (<sub [::filter-values]))
                                                                 :saving-filter?    (<sub [::saving-filter?])
                                                                 :select-filter-evt ::select-saved-filter
                                                                 :clear-search-evt  ::clear-search
                                                                 :save-evt          ::save-filter
                                                                 :delete-evt        ::delete-filter}
                                                                tr config-loading? user-filters)]
       [search-tools tr config-loading?]
       [filter-common/quick-filter-section (util/mmap {:title                 :dashboard-search/quick-filter
                                                       :quick-filters         (<sub [::user-quick-filters])
                                                       :selected-filter       selected-filter
                                                       :loading?              config-loading?
                                                       :select-filter-evt     ::select-saved-filter
                                                       :save-quick-filter-evt ::save-quick-filter}
                                                      tr user-filters)]]]
     [:div.search-results
      ;; Admins get no initial search here, so an admin that has not searched yet is asked to,
      ;; instead of being told that there is nothing.
      (if (or (<sub [::no-access?])
              (and admin? (not results?) (str/blank? (<sub [::search-term]))))
        [:div.mt-8 (tr [(if admin?
                          :dashboard-search/no-workspaces-admin
                          :dashboard-search/no-workspace-access)])]
        [:<>
         [:div.flex.mt-8.mb-4.justify-between.items-center
          [title-results tr]

          [:div
           (when (:map-search-command search-mode)
             [ui-cs/content-switcher {:items     [:dashboard-search/map-view
                                                  :dashboard-search/list-view]
                                      :val->str  (comp tr vector)
                                      :current   selected-view
                                      :on-change #(>evt [::set-selected-view %])}])]]

         (if (= (<sub [::current-view]) :dashboard-search/list-view)
           (if results?
             [:<>
              [:div.flex.w-full.justify-end
               [:div
                [sr/search-result-sort {:tr           tr
                                        :value        (<sub [::sort-condition])
                                        :on-change    #(>evt [::set-sort-condition %])
                                        :sort-options (:sort-options (<sub [::search-config]))}]]]
              [:div
               [search-results (:render-search-result search-mode)]
               [back-to-top-button tr]
               [load-more tr]]]
             [:p (tr [:dashboard-search/no-results])])
           (when-let [map-center (<sub [::map-center])]
             [dashboard-map/map-and-details (util/mmap {:search-opts      (<sub [::map-search-opts])
                                                        :domain-group-ids (<sub [::domain-group-ids])}
                                                       tr
                                                       map-center)]))])]]))
